import { clientEvents } from '@/services/telemetry/client-events';
import { sentryService } from '@/services/telemetry/sentry';
import { track } from '@/shared/utils/track-telemetry';
import { inputFingerprint, safeHostname } from '../gophoto-scan-parser.js';
import type { ScanInputBuffer } from './scanInputBuffer';

interface ScanClassification {
  type: string;
}

interface ScanExtraction {
  code: string | null;
  hostname: string | null;
  strategy: string | null;
}

interface ScanSuccessTiming {
  attemptCount: number;
  submitDuration: number;
  totalScanDuration: number | null;
}

export function trackScanMounted(noInputTimeoutMs: number): void {
  track('[Sentry] Breadcrumb failed:', () => {
    clientEvents.record(
      'scanner_view_ready',
      { timeout_ms: noInputTimeoutMs },
      { area: 'scanner' }
    );
    sentryService.addBreadcrumb(
      'gophoto.scan',
      'GoPhotoScan component mounted, waiting for QR code input',
      { timestamp: new Date().toISOString() }
    );
  });
}

export function trackNoInput(noInputTimeoutMs: number): void {
  track('[Sentry] No-input tracking failed:', () => {
    clientEvents.record(
      'scanner_no_input',
      { timeout_ms: noInputTimeoutMs },
      { area: 'scanner', level: 'warning' }
    );
    sentryService.incrementCounter('gophoto.scan.no_input_30s', 1, {
      component: 'GoPhotoScan',
    });
    sentryService.addBreadcrumb(
      'gophoto.scan',
      'No input received within 30s of mount',
      { timeoutMs: noInputTimeoutMs },
      'warning'
    );
  });
}

export function trackBufferedInput(buffer: ScanInputBuffer): void {
  track('[Sentry] Buffer breadcrumb failed:', () => {
    sentryService.addBreadcrumb(
      'gophoto.scan',
      `Input buffered: ${buffer.reason}`,
      {
        ...inputFingerprint(buffer.input),
        bufferStatus: buffer.status,
        reason: buffer.reason,
      }
    );
  });
}

export function trackInputClassified(
  input: string,
  classification: ScanClassification,
  attemptCount: number
): void {
  track('[Sentry] Classify breadcrumb failed:', () => {
    clientEvents.record(
      'scanner_input_classified',
      {
        ...inputFingerprint(input),
        classification: classification.type,
        attempt_number: attemptCount,
      },
      { area: 'scanner' }
    );
    sentryService.addBreadcrumb(
      'gophoto.scan',
      `Input classified: ${classification.type}`,
      {
        ...inputFingerprint(input),
        classification: classification.type,
        attemptNumber: attemptCount,
      }
    );
  });
}

export function trackExtraction(
  classification: ScanClassification,
  extraction: ScanExtraction
): void {
  track('[Sentry] Extract breadcrumb failed:', () => {
    sentryService.addBreadcrumb(
      'gophoto.scan',
      `Extraction: ${extraction.strategy || 'none'}`,
      {
        strategy: extraction.strategy,
        success: extraction.code !== null,
        classification: classification.type,
        ...(extraction.hostname && { hostname: extraction.hostname }),
      },
      extraction.code ? 'info' : 'warning'
    );
  });
}

export function trackLooseMatch(input: string, hostname: string | null): void {
  track('[Sentry] Loose-match tracking failed:', () => {
    sentryService.incrementCounter('gophoto.scan.loose_match_used', 1, {
      hostname: hostname || 'unknown',
    });
    sentryService.captureMessage?.(
      'GoPhoto loose-fallback URL match used (strict regex failed)',
      {
        level: 'warning',
        tags: {
          component: 'GoPhotoScan',
          operation: 'url_parse_loose',
        },
        extra: { hostname, inputLength: input.length },
      }
    );
  });
}

export function trackExtractedButInvalid(reason: unknown): void {
  track('[Sentry] Validation tracking failed:', () => {
    sentryService.incrementCounter('gophoto.scan.extracted_but_invalid', 1, {
      reason,
    });
  });
}

export function trackRejectedUrl(
  input: string,
  classificationType: 'gophoto_url_malformed' | 'unknown_url'
): void {
  if (classificationType === 'gophoto_url_malformed') {
    track('[Sentry] Error tracking failed:', () => {
      clientEvents.record(
        'scanner_failed',
        {
          reason: classificationType,
          hostname: safeHostname(input),
          ...inputFingerprint(input),
        },
        { area: 'scanner', level: 'warning' }
      );
      sentryService.captureException(new Error('Failed to parse GoPhoto URL'), {
        tags: { component: 'GoPhotoScan', operation: 'url_parse' },
        extra: {
          ...inputFingerprint(input),
          hostname: safeHostname(input),
        },
        level: 'warning',
      });
      sentryService.incrementCounter('gophoto.scan.invalid_url', 1, {
        component: 'GoPhotoScan',
      });
    });
    return;
  }

  track('[Sentry] Unknown-URL tracking failed:', () => {
    clientEvents.record(
      'scanner_failed',
      { reason: classificationType, hostname: safeHostname(input) },
      { area: 'scanner', level: 'warning' }
    );
    sentryService.incrementCounter('gophoto.scan.unknown_url', 1, {
      hostname: safeHostname(input) || 'unknown',
    });
    sentryService.addBreadcrumb(
      'gophoto.scan',
      'Input was a URL but no code could be extracted',
      { hostname: safeHostname(input) },
      'warning'
    );
  });
}

export function trackUnrecognizedFormat(
  input: string,
  sanitized: string
): void {
  track('[Sentry] Unrecognized-format tracking failed:', () => {
    sentryService.incrementCounter('gophoto.scan.unrecognized_format', 1, {
      component: 'GoPhotoScan',
      sanitizedLength: String(sanitized.length),
    });
    sentryService.addBreadcrumb(
      'gophoto.scan',
      'Input did not yield a 6-char code after sanitization',
      {
        ...inputFingerprint(input),
        sanitizedLength: sanitized.length,
      },
      'warning'
    );
  });
}

export function trackInvalidCode(code: string): void {
  track('[Sentry] Error tracking failed:', () => {
    clientEvents.record(
      'scanner_failed',
      { reason: 'invalid_code_format', code_length: code.length },
      { area: 'scanner', level: 'warning' }
    );
    sentryService.addBreadcrumb(
      'gophoto.scan',
      'Invalid code format rejected',
      { code, codeLength: code.length, error: 'invalid_format' },
      'warning'
    );
    sentryService.incrementCounter('gophoto.scan.invalid_code_format', 1, {
      component: 'GoPhotoScan',
    });
  });
}

export function trackSubmitStarted(attemptCount: number): void {
  track('[Sentry] Breadcrumb failed:', () => {
    sentryService.addBreadcrumb(
      'gophoto.scan',
      'Submitting GoPhoto code for registration',
      { attemptNumber: attemptCount }
    );
  });
}

export function withRegistrationSpan<T>(
  attemptCount: number,
  action: () => Promise<T>
): Promise<T> {
  return sentryService.withSpan(
    'gophoto.register_code',
    'gophoto.api',
    action,
    { 'gophoto.attempt_count': attemptCount }
  );
}

export function trackRegistrationSuccess({
  attemptCount,
  submitDuration,
  totalScanDuration,
}: ScanSuccessTiming): void {
  track('[Sentry] Success tracking failed:', () => {
    clientEvents.record(
      'scanner_code_registered',
      {
        submit_duration_ms: Math.round(submitDuration),
        total_scan_duration_ms: totalScanDuration
          ? Math.round(totalScanDuration)
          : null,
        attempt_count: attemptCount,
      },
      { area: 'scanner' }
    );
    sentryService.addBreadcrumb(
      'gophoto.scan',
      'GoPhoto code registered successfully',
      {
        submitDurationMs: Math.round(submitDuration),
        totalScanDurationMs: totalScanDuration
          ? Math.round(totalScanDuration)
          : null,
        attemptCount,
      }
    );
    sentryService.trackMetric(
      'gophoto.scan.submit_duration',
      submitDuration,
      'millisecond',
      { success: 'true' }
    );
    if (totalScanDuration) {
      sentryService.trackMetric(
        'gophoto.scan.total_duration',
        totalScanDuration,
        'millisecond',
        { attemptCount: String(attemptCount) }
      );
    }
    sentryService.incrementCounter('gophoto.scan.success', 1, {
      component: 'GoPhotoScan',
    });
  });
}

export function trackRegistrationFailure(submitDuration: number): void {
  track('[Sentry] Failure tracking failed:', () => {
    clientEvents.record(
      'scanner_failed',
      {
        reason: 'backend_rejected',
        submit_duration_ms: Math.round(submitDuration),
      },
      { area: 'scanner', level: 'error' }
    );
    sentryService.addBreadcrumb(
      'gophoto.scan',
      'GoPhoto code registration failed (backend rejected)',
      {
        submitDurationMs: Math.round(submitDuration),
        error: 'registration_failed',
      },
      'error'
    );
    sentryService.incrementCounter('gophoto.scan.registration_failed', 1, {
      component: 'GoPhotoScan',
      reason: 'backend_rejected',
    });
  });
}

export function trackSubmissionError(
  value: unknown,
  attemptCount: number,
  submitDuration: number,
  totalDuration: number | null
): void {
  const error = value instanceof Error ? value : new Error(String(value));
  track('[Sentry] Exception capture failed:', () => {
    clientEvents.record(
      'scanner_failed',
      {
        reason: 'exception',
        error_name: error.name,
        error_message: error.message,
        submit_duration_ms: Math.round(submitDuration),
      },
      { area: 'scanner', level: 'error' }
    );
    sentryService.captureException(error, {
      tags: {
        component: 'GoPhotoScan',
        operation: 'submit_gophoto_code',
      },
      extra: {
        attemptCount,
        submitDurationMs: Math.round(submitDuration),
        totalDurationMs: totalDuration,
      },
      level: 'error',
    });
    sentryService.addBreadcrumb(
      'gophoto.scan',
      'GoPhoto code submission threw an exception',
      {
        errorMessage: error.message,
        submitDurationMs: Math.round(submitDuration),
      },
      'error'
    );
    sentryService.incrementCounter('gophoto.scan.error', 1, {
      component: 'GoPhotoScan',
      errorType: error.name || 'unknown',
    });
  });
}

export function trackNavigation(
  requiresGdprConsent: boolean,
  nextStep: string
): void {
  track('[Sentry] Navigation breadcrumb failed:', () => {
    sentryService.addBreadcrumb(
      'gophoto.scan',
      `Navigating to next step: ${nextStep}`,
      { requiresGdprConsent, nextStep }
    );
  });
}

export function trackClear(hadError: boolean): void {
  track('[Sentry] Clear breadcrumb failed:', () => {
    sentryService.addBreadcrumb('gophoto.scan', 'Code input cleared', {
      hadError,
    });
  });
}
